import type { FC } from "react";

import { useUser } from "@ui/hooks/useUser";
import { useTranslations } from "@ui/index";

import { useConfig } from "@jti/app-context/useConfig";
import { ChevronBackwardIcon } from "@jti/brand-icons/chevron-backward";

import { getAccountLinks } from "../../../../../Header/components/UserActions/utils/getProfileDropdownLink";
import type { AccountDropdownSettings } from "../../../../Header";
import { getFullUserName } from "../../../UserActions/utils/getFullUserName";
import { HeaderNavListItem } from "../HeaderNavListItem";
import { NavPanel } from "../NavPanel";

import styles from "../../MobileNavigation.module.scss";

type AccountActionsPanelProps = {
  panelId: string;
  isActive: boolean;
  accountDropdownSettings: AccountDropdownSettings;
  onBack: () => void;
  onLogOut: () => void;
  isLogOutPending: boolean;
  logoutLabel: string;
  panelRef: (node: HTMLElement | null) => void;
};

export const AccountActionsPanel: FC<AccountActionsPanelProps> = ({
  panelId,
  isActive,
  accountDropdownSettings,
  onBack,
  onLogOut,
  isLogOutPending,
  logoutLabel,
  panelRef,
}) => {
  const { user } = useUser();
  const { __ } = useTranslations();
  const { isSingleNameField, isReversedNameOrder } = useConfig();
  const titleId = `${panelId}-title`;
  const userName = user ? getFullUserName(__, user, isSingleNameField, isReversedNameOrder) : "";

  const accountLinks = getAccountLinks(accountDropdownSettings);

  return (
    <NavPanel id={panelId} isActive={isActive} labelledById={titleId} ref={panelRef}>
      <header className={styles.panelHeader}>
        <HeaderNavListItem
          label={userName}
          startIcon={ChevronBackwardIcon}
          id={titleId}
          onClick={onBack}
          hasTextEmphasized={true}
        />
      </header>
      <ul className={styles.list}>
        {accountLinks.map(({ href, label }) => (
          <li key={`${label}-${href}`}>
            <HeaderNavListItem variant="link" label={label} href={href} />
          </li>
        ))}
        {logoutLabel && (
          <li>
            <HeaderNavListItem label={logoutLabel} onClick={onLogOut} disabled={isLogOutPending} />
          </li>
        )}
      </ul>
    </NavPanel>
  );
};
